import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Package, Plus, Trash2, Phone } from "lucide-react";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const photoUrl = (p) => (p ? `${API}/files/${p}` : null);

const MujahezTab = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchList = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/mujahez`);
      setList(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/admin/mujahez`, { full_name: name, phone, password });
      toast.success("تم إنشاء حساب المجهّز");
      setName(""); setPhone(""); setPassword("");
      setShowForm(false);
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الإنشاء");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (m) => {
    if (!(await confirmDialog(`حذف المجهّز "${m.full_name}"؟`))) return;
    try {
      await axios.delete(`${API}/admin/mujahez/${m.id}`);
      toast.success("تم الحذف");
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الحذف");
    }
  };

  if (loading) return <p className="text-center text-[#5C6E64] py-8">جاري التحميل...</p>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-heading font-bold text-xl text-[#111814] flex items-center gap-2">
          <Package className="w-5 h-5 text-[#7ED321]" />
          المجهّزون ({list.length})
        </h2>
        <button onClick={() => setShowForm((s) => !s)} data-testid="add-mujahez-btn"
          className="bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold px-4 py-2 rounded-full text-sm flex items-center gap-1">
          <Plus className="w-4 h-4" />إضافة مجهّز
        </button>
      </div>

      {showForm && (
        <motion.form initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
          onSubmit={handleCreate} className="bg-[#F0FDF4] border border-[#7ED321]/30 rounded-2xl p-4 space-y-3">
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="اسم المجهّز"
            data-testid="new-mujahez-name" required
            className="w-full px-4 py-2.5 rounded-2xl border border-[#E4EBE6] bg-white" />
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, ""))} placeholder="07XXXXXXXXX"
            data-testid="new-mujahez-phone" required
            className="w-full px-4 py-2.5 rounded-2xl border border-[#E4EBE6] bg-white" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="كلمة السر (6+ أحرف)"
            data-testid="new-mujahez-password" required minLength={6}
            className="w-full px-4 py-2.5 rounded-2xl border border-[#E4EBE6] bg-white" />
          <button type="submit" disabled={busy} data-testid="new-mujahez-submit"
            className="w-full bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-2.5 rounded-full disabled:opacity-50">
            {busy ? "جاري الإنشاء..." : "إنشاء"}
          </button>
        </motion.form>
      )}

      {list.length === 0 ? (
        <p className="text-center text-[#5C6E64] py-8">لا يوجد مجهّزون. أضف واحداً.</p>
      ) : (
        <div className="space-y-2">
          {list.map((m) => (
            <div key={m.id} data-testid={`mujahez-row-${m.id}`}
              className="bg-white border border-[#E4EBE6] rounded-2xl p-3 flex items-center gap-3">
              {photoUrl(m.photo_path) ? (
                <img src={photoUrl(m.photo_path)} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-[#7ED321]" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#F0FDF4] border-2 border-[#7ED321] flex items-center justify-center">
                  <Package className="w-5 h-5 text-[#7ED321]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#111814]">{m.full_name}</p>
                <a href={`tel:${m.phone}`} className="text-xs text-[#E85D45] flex items-center gap-1 hover:underline">
                  <Phone className="w-3 h-3" />{m.phone}
                </a>
                <div className="flex gap-2 mt-1 text-xs text-[#5C6E64]">
                  <span>بانتظار: <b>{m.pending_count || 0}</b></span>
                  <span>جاهزة: <b>{m.ready_count || 0}</b></span>
                  <span>منجزة: <b>{m.completed_count || 0}</b></span>
                </div>
              </div>
              <button onClick={() => handleDelete(m)} data-testid={`delete-mujahez-${m.id}`}
                className="bg-[#E85D45] hover:bg-[#c14a36] text-white text-xs font-bold p-2 rounded-full">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MujahezTab;
